import { useMemo, useId } from 'react';
import Chart from 'react-apexcharts';
import type { BarSegments } from '../dora/codeCompositionUtils';
import {
  formatCompositionPercent,
  formatLineCount,
} from '../dora/codeCompositionUtils';
import { getDarkDonutChartBase, mergeChartOptions } from '../../utils/darkChartTheme';

const DONUT_COLORS = ['#10b981', '#6366f1', '#f59e0b'] as const;

const LEGEND_ITEMS = [
  { key: 'new' as const, label: 'New files', colorClass: 'bg-emerald-500' },
  { key: 'existing' as const, label: 'Existing changes', colorClass: 'bg-indigo-500' },
  { key: 'rework' as const, label: 'Rework', colorClass: 'bg-amber-500' },
];

const DONUT_SIZE = 200;

function buildAriaLabel(segments: BarSegments): string {
  if (!segments.hasData) return 'No composition data';
  return `Code composition: ${formatCompositionPercent(segments.newPct)} new files, ${formatCompositionPercent(segments.existingPct)} existing changes, ${formatCompositionPercent(segments.reworkPct)} rework`;
}

function segmentValues(segments: BarSegments): number[] {
  if (!segments.hasData) return [1];
  const values = [segments.newLines, segments.existingLines, segments.reworkLines];
  const total = values.reduce((a, b) => a + b, 0);
  if (total <= 0) return [1];
  return values;
}

type CodeCompositionDonutChartProps = {
  segments: BarSegments;
  className?: string;
};

export default function CodeCompositionDonutChart({
  segments,
  className = '',
}: CodeCompositionDonutChartProps) {
  const chartUid = useId().replace(/:/g, '');
  const series = useMemo(() => segmentValues(segments), [segments]);
  const labels = useMemo(
    () => LEGEND_ITEMS.map((item) => item.label),
    []
  );

  const pctByKey = {
    new: segments.newPct,
    existing: segments.existingPct,
    rework: segments.reworkPct,
  };

  const lineByKey = {
    new: segments.newLines,
    existing: segments.existingLines,
    rework: segments.reworkLines,
  };

  const chartOptions = useMemo(
    () =>
      mergeChartOptions(getDarkDonutChartBase(), {
        chart: {
          id: `code-composition-donut-${chartUid}`,
          type: 'donut',
          animations: { enabled: true, easing: 'easeinout', speed: 400 },
        },
        colors: [...DONUT_COLORS],
        labels,
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: { width: 2, colors: ['#0f1117'] },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: { show: false },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (value: number, { seriesIndex }: { seriesIndex: number }) => {
              const key = LEGEND_ITEMS[seriesIndex]?.key;
              if (!key || !segments.hasData) return `${value}`;
              const pct =
                key === 'new'
                  ? segments.newPct
                  : key === 'existing'
                    ? segments.existingPct
                    : segments.reworkPct;
              const lines =
                key === 'new'
                  ? segments.newLines
                  : key === 'existing'
                    ? segments.existingLines
                    : segments.reworkLines;
              return `${formatCompositionPercent(pct)} (${formatLineCount(lines)} lines)`;
            },
          },
        },
        states: {
          hover: { filter: { type: 'lighten', value: 0.08 } },
        },
      }),
    [chartUid, labels, segments]
  );

  return (
    <div
      className={`flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8 ${className}`}
      role="img"
      aria-label={buildAriaLabel(segments)}
    >
      <div className="relative shrink-0" style={{ width: DONUT_SIZE, height: DONUT_SIZE }}>
        <Chart
          type="donut"
          width={DONUT_SIZE}
          height={DONUT_SIZE}
          series={series}
          options={chartOptions as ApexCharts.ApexOptions}
        />
        {segments.hasData ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xs text-slate-400">Total lines</span>
            <span className="text-lg font-semibold tabular-nums text-slate-100">
              {formatLineCount(segments.totalLines)}
            </span>
          </div>
        ) : null}
      </div>

      <ul className="flex flex-col gap-3 text-sm min-w-0">
        {LEGEND_ITEMS.map(({ key, label, colorClass }) => (
          <li key={key} className="flex items-center gap-2 text-slate-300">
            <span
              className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${colorClass}`}
              aria-hidden
            />
            <span className="min-w-0">
              {label}{' '}
              <span className="font-medium text-slate-100 tabular-nums">
                {segments.hasData ? formatCompositionPercent(pctByKey[key]) : 'N/A'}
              </span>
              {segments.hasData ? (
                <span className="text-slate-500 tabular-nums">
                  {' '}
                  · {formatLineCount(lineByKey[key])} lines
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CodeCompositionDonutChartSkeleton() {
  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8 animate-pulse" aria-hidden>
      <div
        className="shrink-0 rounded-full bg-dark-elevated"
        style={{ width: DONUT_SIZE, height: DONUT_SIZE }}
      />
      <div className="flex flex-col gap-3 w-full sm:w-48">
        {LEGEND_ITEMS.map(({ key }) => (
          <div key={key} className="h-4 w-full rounded bg-dark-elevated" />
        ))}
      </div>
    </div>
  );
}
